import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import {
  browserLocalPersistence,
  getAuth,
  onIdTokenChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth'
import { env } from '@/config/env'

/**
 * Firebase Authentication for the admin portal.
 *
 * Firebase owns credentials, renewal and lockout. This module is the only place that
 * touches its SDK, so the rest of the app deals in "a token" and "a user" without
 * knowing which provider produced them.
 *
 * Note what is NOT here: no token is written to localStorage by our code. The SDK keeps
 * its own refresh token in IndexedDB and mints short-lived ID tokens on demand. That is
 * the main practical gain over the JWT scheme this replaced, where a long-lived refresh
 * token sat in localStorage and any XSS bug on this origin was an account takeover.
 */

let app: FirebaseApp | undefined
let auth: Auth | undefined

function ensureAuth(): Auth {
  if (auth) return auth

  // getApps() guard: Vite's HMR re-runs this module, and initializeApp throws on a
  // duplicate app name.
  app = getApps()[0] ?? initializeApp(env.firebase)
  auth = getAuth(app)

  // Survive a reload. Without this the SDK defaults can drop the session on refresh,
  // which would sign staff out every time they hit F5.
  void setPersistence(auth, browserLocalPersistence)

  return auth
}

export type FirebaseUser = User

/** Signs in. Throws a FirebaseError whose `code` the login screen maps to a message. */
export async function signIn(email: string, password: string): Promise<User> {
  const credential = await signInWithEmailAndPassword(ensureAuth(), email.trim(), password)
  return credential.user
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(ensureAuth())
}

export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(ensureAuth(), email.trim())
}

/**
 * The current ID token, refreshed automatically when it is close to expiry.
 *
 * Read per request rather than cached: an ID token lives about an hour, and a cached one
 * would start 401ing mid-session. The SDK does the renewal, so this is cheap.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const current = ensureAuth().currentUser
  if (!current) return null

  try {
    return await current.getIdToken(forceRefresh)
  } catch {
    // Offline, or the account was disabled or deleted in Firebase. Treat as signed out
    // rather than throwing into whichever request happened to ask.
    return null
  }
}

/**
 * Fires on sign-in, sign-out, and every silent token refresh.
 *
 * `onIdTokenChanged` rather than `onAuthStateChanged`: the latter ignores renewals, so a
 * disabled account would keep its session alive until the tab was reloaded.
 */
export function onAuthChanged(callback: (user: User | null) => void): () => void {
  return onIdTokenChanged(ensureAuth(), callback)
}

/**
 * Turns Firebase's error codes into something a person can act on.
 *
 * `invalid-credential` deliberately does not distinguish a wrong password from an unknown
 * address — that difference tells an attacker which addresses are staff.
 */
export function friendlyAuthError(error: unknown): string {
  const code = (error as { code?: string })?.code ?? ''

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email address and password do not match an account.'
    case 'auth/invalid-email':
      return 'That does not look like an email address.'
    case 'auth/user-disabled':
      return 'This account has been disabled. Ask an administrator to re-enable it.'
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.'
    case 'auth/network-request-failed':
      return 'Could not reach the authentication service. Check your connection.'
    case 'auth/operation-not-allowed':
      return 'Email and password sign-in is not enabled for this Firebase project.'
    default:
      return 'Could not sign you in. Please try again.'
  }
}
